import React, { useState } from 'react';
import { useToast } from '@/hooks/errors/use-toast';
import { useImageGeneration as useRunwareImageGeneration } from '@/hooks/runware';
import { useImageLibrary } from '@/hooks/runware/use-image-library';
import { useQueryClient } from '@tanstack/react-query';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { ImageType } from '@/components/image-generator/FilterBar/TypeFilter';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';

interface UseImageGenerationOptions {
  imageDescription: string;
  width: string;
  height: string;
  selectedType: ImageType | null;
  selectedStyle: string | null; // Updated to allow null
  selectedInfluence: string | null; // Updated to allow null
  selectedBackground: string | null; // Updated to allow null
  styleOptions: FilterOption[];
  influenceOptions: FilterOption[];
  colorOptions: FilterOption[];
  backgroundOptions: FilterOption[];
  environmentOptions: FilterOption[];
  moodOptions: FilterOption[];
}

export const useResourceImageGeneration = (options: UseImageGenerationOptions) => {
  const {
    imageDescription,
    width,
    height,
    selectedType,
    selectedStyle,
    selectedInfluence,
    selectedBackground,
    styleOptions,
    influenceOptions,
    colorOptions,
    backgroundOptions,
    environmentOptions,
    moodOptions
  } = options;

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const usageData = useAIUsageData();
  const { openOutOfCreditsModal } = useGlobalModals();
  
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  
  // Use consolidated image generation hook
  const { generateImage } = useRunwareImageGeneration();

  // Use image library hook
  const { saveImageToLibrary, setSearchTerm } = useImageLibrary();

  // Map frontend image type strings to backend IDs
  const getImageTypeId = (type: ImageType | null): string | undefined => {
    if (!type) return undefined;
    
    const typeMap: Record<string, string> = {
      'Photo': 'type-photo',
      'Art': 'type-art', 
      'Anime': 'type-anime',
      '3D': 'type-3d'
    };
    
    return typeMap[type];
  };

  // Function to generate image using backend prompt building
  const handleGenerateImage = async () => {
    console.log('[ResourcesContainer] Generating image');
    
    // Validate image description is provided
    if (!imageDescription.trim()) {
      console.log('[ResourcesContainer] Empty image description. Cannot generate image.');
      toast({
        title: "Description Required",
        description: "Please provide an image description before generating an image.",
        variant: "error",
      });
      return;
    }

    // ✅ UPFRONT CREDIT CHECK - Prevent operation if no credits available
    if (usageData.remainingBalance === 0) {
      console.log('[ResourcesContainer] Insufficient credits - blocking image generation and showing modal');
      openOutOfCreditsModal();
      return; // Clean early exit - no cascading errors
    }

    console.log(`[ResourcesContainer] Credits available (${usageData.remainingBalance}) - proceeding with image generation`);
    
    // Set generating state
    setIsGenerating(true);
    setGeneratedImage(null);
    
    try {
      console.log('[ResourcesContainer] Calling generateImage with prompt building parameters');
      
      // Collect filter IDs for prompt building
      const activeColorIds = colorOptions.filter(o => o.checked).map(o => o.id);
      const activeEnvironmentIds = environmentOptions.filter(o => o.checked).map(o => o.id);
      const activeMoodIds = moodOptions.filter(o => o.checked).map(o => o.id);
      
      // Map frontend type to backend ID
      const imageTypeId = getImageTypeId(selectedType);
      
      console.log('[ResourcesContainer] Image type mapping:', { selectedType, imageTypeId });
      
      // Call generate image function with prompt building parameters (backend will build the prompt)
      const result = await generateImage({
        additionalText: imageDescription,
        imageType: imageTypeId, // Add the mapped image type ID
        styleId: selectedStyle || undefined,
        influenceId: selectedInfluence || undefined,
        backgroundId: selectedBackground || undefined,
        environmentIds: activeEnvironmentIds.length > 0 ? activeEnvironmentIds : undefined,
        moodIds: activeMoodIds.length > 0 ? activeMoodIds : undefined,
        colorIds: activeColorIds.length > 0 ? activeColorIds : undefined,
        width: parseInt(width) || 512,
        height: parseInt(height) || 512,
        numberResults: 1,
        metadata: {
          styleId: selectedStyle,
          influenceId: selectedInfluence,
          colorIds: activeColorIds,
          backgroundId: selectedBackground,
          environmentIds: activeEnvironmentIds,
          moodIds: activeMoodIds,
          imageType: selectedType
        }
      });
      
      console.log('[ResourcesContainer] Image generation result:', result);
      
      if (result && result.length > 0) {
        setGeneratedImage(result[0].imageURL);
        toast({
          title: "Image Generated",
          description: "Your image has been successfully generated!",
        });
        
        // Set image loading state
        setIsImageLoading(true);
        
        // Save the generated image to library
        if (result[0]) {
          const savedImage = await saveImageToLibrary({
            generatedImage: result[0],
            imageType: selectedType,
            styleId: selectedStyle,
            influenceId: selectedInfluence,
            backgroundId: selectedBackground,
            environmentIds: activeEnvironmentIds,
            moodIds: activeMoodIds,
            colorIds: activeColorIds,
            width: parseInt(width),
            height: parseInt(height),
            description: imageDescription,
            cost: result[0].totalCost || result[0].cost || 0,
            fullPrompt: result[0].fullPrompt || result[0].positivePrompt || ''
          });
          
          // Only set the saved image as selected after confirming it's loaded
          if (savedImage) {
            // Force invalidate the image library cache to show new image immediately
            console.log('[ResourcesContainer] Invalidating image library cache after save');
            queryClient.invalidateQueries({ queryKey: ['imageLibrary'] });
            
            // Create a new Image object to check when it's fully loaded
            const img = new Image();
            img.onload = () => {
              // Image is fully loaded, now open the modal
              setSelectedImage(savedImage);
              setIsImageLoading(false);
            };
            img.onerror = () => {
              // Handle image loading error
              console.error('[ResourcesContainer] Error loading image');
              toast({
                title: "Image Loading Failed",
                description: "The image was generated but couldn't be loaded properly.",
                variant: "error",
              });
              setIsImageLoading(false);
            };
            // Start loading the image
            img.src = savedImage.image_url;
          } else {
            setIsImageLoading(false);
          }
        } else {
          setIsImageLoading(false);
        }
      }
    } catch (error) {
      console.error('[ResourcesContainer] Error generating image:', error);
      toast({
        title: "Generation Failed",
        description: "Failed to generate image. Please try again.",
        variant: "error",
      });
      setIsImageLoading(false);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle image description changes
  const handleImageDescriptionChange = (value: string) => {
    // This line updates the search term in the ImageLibrary context
    setSearchTerm(value);
  };

  return {
    isGenerating,
    generatedImage,
    selectedImage,
    setSelectedImage,
    isImageLoading,
    handleGenerateImage,
    handleImageDescriptionChange
  };
};
